import React, { useState, useEffect, useRef } from 'react';
import { Table, Form, Input, Select, DatePicker, Space, Button, message } from 'antd';
import { SearchOutlined, ReloadOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { getSalesOrders, getSalesPlatforms } from '../../../services/api';

const { RangePicker } = DatePicker;

export default function OrderQuery() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [platforms, setPlatforms] = useState([]);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const pageRef = useRef(page);
  const searchTimer = useRef(null);

  useEffect(() => { pageRef.current = page; }, [page]);

  const debouncedSearch = () => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      if (pageRef.current === 1) fetchData();
      else setPage(1);
    }, 300);
  };
  const cancelSearch = () => clearTimeout(searchTimer.current);

  const fetchData = async () => {
    setLoading(true);
    try {
      const values = form.getFieldsValue();
      const params = { page, pageSize: 20 };
      if (values.dateRange) {
        params.startDate = values.dateRange[0].format('YYYY-MM-DD');
        params.endDate = values.dateRange[1].format('YYYY-MM-DD');
      }
      if (values.shopName) params.shopName = values.shopName;
      if (values.shopSite) params.shopSite = values.shopSite;
      if (values.status) params.status = values.status;
      const res = await getSalesOrders(params);
      setData(res.data.list);
      setTotal(res.data.total);
    } catch (err) { message.error(err.message); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [page]);

  useEffect(() => {
    getSalesPlatforms().then(res => setPlatforms(res.data || [])).catch(() => {});
  }, []);

  const columns = [
    { title: '订单号', dataIndex: 'soId' },
    { title: '店铺', dataIndex: 'shopName', ellipsis: true },
    { title: '平台', dataIndex: 'shopSite' },
    { title: '金额', dataIndex: 'amount', render: (v) => `¥${Number(v).toLocaleString()}` },
    { title: '状态', dataIndex: 'status' },
    { title: '下单时间', dataIndex: 'orderDate', render: (v) => v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-' },
    { title: '操作', render: (_, r) => (
      <Button type="link" icon={<EyeOutlined />} onClick={() => navigate(`/sales/orders/${r.soId}`)}>详情</Button>
    )},
  ];

  return (
    <div>
      <Form form={form} layout="inline" style={{ marginBottom: 16 }}>
        <Form.Item name="dateRange"><RangePicker /></Form.Item>
        <Form.Item name="shopSite">
          <Select allowClear placeholder="选择平台" style={{ width: 120 }}
            options={platforms.map(p => ({ label: p, value: p }))} />
        </Form.Item>
        <Form.Item name="shopName"><Input placeholder="店铺名称" allowClear onChange={debouncedSearch} /></Form.Item>
        <Form.Item name="status">
          <Select allowClear placeholder="订单状态" style={{ width: 140 }}
            options={['Cancelled','Merged','Question','Sent','Split','WaitConfirm','WaitPay','取消','已付款待审核','已发货','异常','待付款','被拆分'].map(s => ({ label: s, value: s }))} />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" icon={<SearchOutlined />} onClick={() => { cancelSearch(); setPage(1); fetchData(); }}>查询</Button>
            <Button icon={<ReloadOutlined />} onClick={() => { cancelSearch(); form.resetFields(); setPage(1); fetchData(); }}>重置</Button>
          </Space>
        </Form.Item>
      </Form>
      <Table dataSource={data} columns={columns} rowKey="id" loading={loading}
        pagination={{ current: page, total, pageSize: 20, onChange: setPage, showTotal: (t) => `共 ${t} 条` }} />
    </div>
  );
}
